<template>
  <div class="slo-add-to-cart">
    <div :class="{
      'slo-add-to-cart--wrapper': true,
      [`slo-add-to-cart--wrapper--${maxWidth}`]: maxWidth
    }">
      <slot />
      <SloNumberInput
        v-if="showNumberInput"
        :key="`add-to-cart-${sku}`"
        :initialValue="quantity"
        :min="1"
        :min-error-text="'You must add a quantity of at least one for an item.' | t"
        :max="999"
        :max-error-text="'You can only order a maximum of 999 quantity for an item.'| t"
        @inputUpdate="handleQuantityInputUpdate($event)"
        @valueUpdate="handleQuantityValueUpdate($event)"
      />
      <SloButton
        :disabled="quantity < 1 || quantity > 999"
        @click="handleAddToCart"
        :fill="addToCartButtonFill"
        v-html="buttonText"
        class="slo-add-to-cart--button sm:w-100"
      />
    </div>
    <transition name="fade-up">
      <SloModal
        v-if="showSubscriptionRemovalModal"
        :maxWidth="500"
        @dismiss="handleCloseSubscriptionRemovalModal"
      >
        <template v-slot:title>
          {{ 'Attention' | t }}
        </template>
        <div class="px-sm py-md bg-gray-50 rounded-md">
          <p class="m-0">
            {{ 'Adding this item will replace the existing Subscribe & Save item in the cart.' | t }}
          </p>
        </div>
        <template v-slot:footer>
          <SloButton
            @click="handleCloseSubscriptionRemovalModal"
            :disabled="loading"
            fill="outline"
          >
            {{ 'Discard Changes' | t }}
          </SloButton>
          <SloButton @click="handleChangeToOneTimePurchase" :disabled="loading" fill="solid">
            <SloLoader :loading="loading" color="white" size="md" class="p-0">
              {{ 'Add to Cart' | t }}
            </SloLoader>
          </SloButton>
        </template>
      </SloModal>
    </transition>
  </div>
</template>

<script>
import {mapGetters, mapState, mapActions} from 'vuex';
import t from '../../js/main/i18n';
import {handleApiErrors, tagManagerTrack, findCartEntry} from '../../js/main/utils';
import SloButton from './SloButton.vue';
import SloNumberInput from './SloNumberInput.vue';
import SloField from './SloField.vue';
import SloSelect from './SloSelect.vue';
import SloModal from './SloModal.vue';
import SloLoader from './SloLoader.vue';

export default {
  name: 'SloAddToCart',
  components: {
    SloButton,
    SloNumberInput,
    SloField,
    SloSelect,
    SloModal,
    SloLoader
  },
  filters: {
    t
  },
  props: {
    sku: {type: String, required: true},
    showNumberInput: {type: Boolean, default: true},
    addToCartButtonFill: {type: String, default: 'solid'},
    maxWidth: {type: String, default: 'md'},
    listName: {type: String, default: ''}
  },
  data() {
    return {
      loading: false,
      quantity: 1,
      showSubscriptionRemovalModal: false,
      buttonText: t('Add to Cart'),
      cartEntry: false
    };
  },
  computed: {
    ...mapGetters('auth', ['user']),
    ...mapGetters('marketplace', ['cartId', 'cartEntries']),
    ...mapState('marketplace', ['cartResponse'])
  },
  created() {
    this.evaluateExistingQuantityInCart();
  },
  watch: {
    cartEntries() {
      this.evaluateExistingQuantityInCart();
    }
  },
  methods: {
    ...mapActions('marketplace', ['addToCart', 'removeCartEntry']),
    evaluateExistingQuantityInCart() {
      this.findCartEntryMatch();
      if (this.cartEntry) {
        this.quantity = this.cartEntry?.quantity;
        this.buttonText = t('Update Quantity');
      } else {
        this.quantity = 1;
        this.buttonText = t('Add to Cart');
      }
    },
    async handleAddToCart() {
      if (this.findCartEntryMatch()?.subscriptionFrequency) {
        this.showSubscriptionRemovalModal = true;
        return;
      }

      this.loading = true;

      const data = {
        sku: this.sku,
        quantity: this.quantity,
        entryNumber: this.cartEntry?.entryNumber ?? false
      };

      await this.addToCart(data)
        .then(() => {
          this.loading = false;
          this.showSubscriptionRemovalModal = false;
          this.$emit('done');

          const cartEntry = findCartEntry(this.cartEntries, this.sku);
          if (cartEntry) {
            tagManagerTrack('add_to_cart', {
              currency: this.cartResponse?.subTotal?.currencyIso,
              user: this.user?.accountNumber || 'guest',
              cartId: this.cartId,
              cartSubTotal: this.cartResponse?.subTotal?.value,
              items: [
                {
                  item_id: this.sku,
                  item_name: cartEntry?.product?.number,
                  item_category: cartEntry?.product?.type,
                  item_list_name: this.listName,
                  price: cartEntry?.product?.price?.value,
                  quantity: this.quantity,
                  totalPrice: cartEntry?.totalPrice?.value
                }
              ]
            });
          }
        })
        .catch(e => {
          handleApiErrors(e, this);
        });
    },
    findCartEntryMatch() {
      if (!this.cartEntries) return;

      const cartEntry = findCartEntry(this.cartEntries, this.sku);
      if (this.showNumberInput && cartEntry) {
        this.cartEntry = cartEntry;
        return;
      }

      this.cartEntry = false;

      return cartEntry;
    },
    handleQuantityInputUpdate(quantity) {
      this.quantity = parseInt(quantity) || '';
    },
    handleQuantityValueUpdate(quantity) {
      this.quantity = quantity;
    },
    async handleChangeToOneTimePurchase() {
      const cartEntry = this.findCartEntryMatch();
      await this.removeCartEntry({entryNumber: cartEntry?.entryNumber});
      this.quantity = 1;
      await this.handleAddToCart();
    },
    handleCloseSubscriptionRemovalModal() {
      this.showSubscriptionRemovalModal = false;
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../../scss/system';

.slo-add-to-cart {
  position: relative;

  &--wrapper {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: start;

    row-gap: 0.8rem;
    column-gap: 1.6rem;

    @media (min-width: 768px) {
      flex-direction: row;
      align-items: center;
    }

    &--md {
      @media (min-width: 768px) {
        max-width: 320px;
      }
    }

    &--lg {
      @media (min-width: 768px) {
        max-width: 380px;
      }
    }
  }

  &--button {
    flex: 1;
    &:disabled {
      pointer-events: none;
    }
  }
}

.mini-cart--footer {
  .slo-add-to-cart--wrapper {
    position: static;
    align-items: center;

    @media (min-width: 480px) {
      flex-direction: row;
    }
  }
}
</style>
